import React, { useState, useEffect } from 'react'
import { Box, Typography, Button } from '@mui/material'
import { useDomainManagement } from './hooks/useDomainManagement'
import { useDomainVerification } from './hooks/useDomainVerification'
import ModalAlert from './components/ModalAlert'
import { viewStyles } from './VerifyDomainsView.styles'
import { stepComponents } from './components/steps'
import { useLazyGetPossibleCompanyDomainsQuery } from '../../../../../api/integrations'
import LoadingIndicator from '../../../../../common/components/LoadingIndicator'

const VIEW_STATES = {
  LIST_DOMAINS: 'LIST_DOMAINS',
  DNS_DETAILS: 'DNS_DETAILS',
  EMAIL_SENT: 'EMAIL_SENT'
}

export default function VerifyDomainsView({ onClose, currentStepDetails, completeStepAndProceed }) {
  const { domains, isAddingDomain } = useDomainManagement()
  const [getPossibleCompanyDomains, { data: selectedDomainForVerification, isLoading }] =
    useLazyGetPossibleCompanyDomainsQuery()
  const [currentView, setCurrentView] = useState(VIEW_STATES.LIST_DOMAINS)

  const [newDomainName, setNewDomainName] = useState('')
  const [currentEmailForStep, setCurrentEmailForStep] = useState('')

  const domainVerification = useDomainVerification(selectedDomainForVerification)

  useEffect(() => {
    if (selectedDomainForVerification) {
      setCurrentEmailForStep(selectedDomainForVerification[0] || '')
      switch (domainVerification.verificationStep) {
        case 'DNS_DETAILS':
          setCurrentView(VIEW_STATES.DNS_DETAILS)
          break
        case 'EMAIL_SENT':
          setCurrentView(VIEW_STATES.EMAIL_SENT)
          break
        default:
          break
      }
    } else {
      setCurrentEmailForStep('')
    }
  }, [domainVerification.verificationStep, selectedDomainForVerification, domainVerification.dnsRecords, currentView])

  const handleStartVerification = domain => {
    getPossibleCompanyDomains({ domain } || '')
    domainVerification.setVerificationStep('EMAIL_SENT')
  }

  const handleBackToList = () => {
    setCurrentView(VIEW_STATES.LIST_DOMAINS)
    domainVerification.setError(null)
    domainVerification.setVerificationStep('LIST_DOMAINS')
  }

  const handleSkip = () => {
    if (currentStepDetails && completeStepAndProceed) {
      completeStepAndProceed(currentStepDetails.id, { intent: 'skip' })
      onClose()
    } else {
      console.warn('VerifyDomainsView: Cannot skip step - missing props')
      onClose()
    }
  }

  const handleDnsVerificationAttempt = async () => {
    await domainVerification.attemptDnsVerification()
  }

  const handleEmailResend = () => {
    if (currentEmailForStep) {
      domainVerification.sendVerificationEmail({ email: currentEmailForStep })
    } else {
      console.warn('VerifyDomainsView: No email selected for sending verification.')
      domainVerification.setError('Please select an email address to send the verification to.')
    }
  }

  const getHeaderText = () => {
    switch (currentView) {
      case VIEW_STATES.DNS_DETAILS:
        return ''
      case VIEW_STATES.EMAIL_SENT:
        return 'Choose an email address from which to confirm domain ownership'
      case VIEW_STATES.LIST_DOMAINS:
      default:
        return 'Choose domain you want to verify'
    }
  }

  const availableEmailsForSelectedDomain = selectedDomainForVerification || []
  if (selectedDomainForVerification && selectedDomainForVerification.defaultEmail) {
    if (!availableEmailsForSelectedDomain.includes(selectedDomainForVerification.defaultEmail)) {
      availableEmailsForSelectedDomain.unshift(selectedDomainForVerification.defaultEmail)
    }
  }

  const stepProps = {
    LIST_DOMAINS: {
      newDomainName,
      setNewDomainName,
      isAddingDomain,
      domains,
      handleStartVerification,
      viewStyles
    },
    DNS_DETAILS: {
      domainName: selectedDomainForVerification?.domain,
      dnsRecords: domainVerification.dnsRecords,
      onVerify: handleDnsVerificationAttempt,
      isLoading: domainVerification.isLoading,
      error: domainVerification.error,
      onBack: () => domainVerification.selectVerificationMethod('email')
    },
    EMAIL_SENT: {
      domainName: selectedDomainForVerification?.domain,
      domainUserCount: selectedDomainForVerification?.userCount || 0,
      selectedEmail: currentEmailForStep,
      onSelectedEmailChange: setCurrentEmailForStep,
      availableEmails: availableEmailsForSelectedDomain,
      onInitiateResend: handleEmailResend,
      isLoading: domainVerification.isLoading,
      error: domainVerification.error,
      onBack: handleBackToList,
      resendCooldownActive: domainVerification.resendCooldownActive,
      resendSecondsLeft: domainVerification.resendSecondsLeft,
      isInitialSend: domainVerification.isInitialEmailSend,
      showSuccessNotification: domainVerification.showEmailSuccessNotification
    },
    VERIFYING: {
      domainName: selectedDomainForVerification?.name,
      verificationMethod: currentView === VIEW_STATES.DNS_DETAILS || domainVerification.dnsRecords ? 'DNS' : 'Email',
      isLoading: true
    },
    VERIFIED_SUCCESS: {
      domainName: selectedDomainForVerification?.name,
      onClose: handleBackToList,
      verificationMethod: domainVerification.dnsRecords ? 'DNS' : 'Email'
    }
  }

  const StepComponent = stepComponents[currentView]
  const showBackButton = currentView !== VIEW_STATES.LIST_DOMAINS
  if (isLoading) {
    return <LoadingIndicator />
  }

  return (
    <Box sx={viewStyles.container}>
      <Box sx={viewStyles.headerBox}>
        <Typography gutterBottom sx={viewStyles.headerText} component='div'>
          {getHeaderText()}
        </Typography>
      </Box>

      {domainVerification.error &&
        currentView !== VIEW_STATES.DNS_DETAILS &&
        currentView !== VIEW_STATES.EMAIL_SENT &&
        currentView !== VIEW_STATES.LIST_DOMAINS && (
          <ModalAlert severity='error' message={domainVerification.error} sx={{ mb: 2 }} />
        )}

      <Box sx={viewStyles.contentArea}>{StepComponent && <StepComponent {...stepProps[currentView]} />}</Box>

      <Box
        sx={{
          ...viewStyles.footer,
          display: 'flex',
          justifyContent: showBackButton ? 'space-between' : 'flex-end',
          alignItems: 'center',
          gap: 1
        }}
      >
        {showBackButton && (
          <Button variant='outlined' onClick={handleBackToList} sx={viewStyles.backButton}>
            Back
          </Button>
        )}
        <Button variant='outlined' onClick={handleSkip} sx={viewStyles.doneButton} fullWidth={!showBackButton}>
          {currentStepDetails.skipButtonText}
        </Button>
      </Box>
    </Box>
  )
}
